/**
 * Evidence Chain Page
 * View and export compliance evidence for audits
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Title2,
  Body1,
  Card,
  CardHeader,
  Button,
  Spinner,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Dropdown,
  Option,
  Field,
  Input,
  makeStyles,
  tokens,
  Tab,
  TabList,
  Tooltip,
  ProgressBar,
  Text,
} from '@fluentui/react-components';
import {
  DocumentCheckmarkRegular,
  ArrowDownloadRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  DismissCircleRegular,
  LinkRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { evidenceApi } from '../services/api';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import { EvidenceChainVisualizer } from '../components/evidence/EvidenceChainVisualizer';
import type { EvidenceRecord } from '../types';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  ADAPTIVE_GRID_THRESHOLD,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    padding: tokens.spacingVerticalL,
    maxWidth: '1400px',
    height: '100%',
    minHeight: 0,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  recordsHeading: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  headerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },
  summaryCard: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  summaryValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    lineHeight: '1',
  },
  summaryLabel: {
    color: tokens.colorNeutralForeground3,
  },
  decisionBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalS,
  },
  filterBar: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  chainStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  chainValid: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
  },
  chainInvalid: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  exportDialog: {
    minWidth: '400px',
  },
  formatOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
  },
});

type DatePreset = '7d' | '30d' | 'month' | 'custom';
type ExportFormat = 'json' | 'csv' | 'sarif' | 'pdf';

/** Wraps the (unpaged) `evidenceApi.getRecords` response as a `PagedResult` — `useAdaptiveGridData`
 *  then applies search/sort/paging in memory (client mode), since the evidence stream for a
 *  bounded date range is never large enough to warrant a real server-paged endpoint. */
async function fetchEvidenceRecordsPage(
  from: Date,
  to: Date,
  params: ListQueryParams
): Promise<PagedResult<EvidenceRecord>> {
  const items = await evidenceApi.getRecords({ from, to, limit: ADAPTIVE_GRID_THRESHOLD });
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

export const EvidencePage: React.FC = () => {
  const styles = useStyles();

  // State
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [selectedTab, setSelectedTab] = useState<'overview' | 'records'>('overview');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json');

  // Calculate date range based on preset — memoized so the queryKey is stable
  const { from, to } = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case '7d':
        return { from: subDays(now, 7), to: now };
      case '30d':
        return { from: subDays(now, 30), to: now };
      case 'month':
        return { from: startOfMonth(now), to: endOfMonth(now) };
      case 'custom':
        return {
          from: customFrom ? new Date(customFrom) : subDays(now, 30),
          to: customTo ? new Date(customTo) : now,
        };
    }
  }, [datePreset, customFrom, customTo]);

  // Queries — retry: 1 so the page doesn't spin forever when the API is unreachable
  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useQuery({
    queryKey: ['evidenceSummary', from.toISOString(), to.toISOString()],
    queryFn: () => evidenceApi.getSummary(from, to),
    retry: 1,
  });

  // Evidence Records list — URL-persisted view state (page/sort/search) + adaptive client/server paging.
  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'timestamp:desc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const recordsSortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const recordsGrid = useAdaptiveGridData<EvidenceRecord>({
    queryKeyBase: ['evidence-records', from.toISOString(), to.toISOString()],
    fetchPage: (params) => fetchEvidenceRecordsPage(from, to, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['type', 'decision', 'frameworks'],
    sortState: recordsSortState,
    enabled: selectedTab === 'records',
  });

  const {
    items: evidenceRecords,
    totalCount: recordsTotalCount,
    totalPages: recordsTotalPages,
    hasPreviousPage: recordsHasPreviousPage,
    hasNextPage: recordsHasNextPage,
    isLoading: recordsLoading,
    isFetching: recordsFetching,
    isError: recordsIsError,
    refetch: refetchRecords,
  } = recordsGrid;

  // Out-of-range clamp — never strand the user on an empty page after a date-range/search change.
  useEffect(() => {
    if (selectedTab === 'records' && !recordsLoading && state.page > recordsTotalPages) {
      setPage(recordsTotalPages);
    }
  }, [selectedTab, recordsLoading, state.page, recordsTotalPages, setPage]);

  const { data: chainVerification, isLoading: chainLoading } = useQuery({
    queryKey: ['evidenceChain', from.toISOString(), to.toISOString()],
    queryFn: () => evidenceApi.verifyChain(from, to),
    retry: 1,
  });

  // Mutations
  const exportMutation = useMutation({
    mutationFn: (format: ExportFormat) => evidenceApi.export({ from, to, format }),
    onSuccess: (result) => {
      // Download the file
      const blob = new Blob([result.data], { type: result.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName;
      a.click();
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    },
  });

  if (summaryLoading) {
    return (
      <div className={styles.container}>
        <Spinner label="Loading evidence..." />
      </div>
    );
  }

  // Auth error detection
  const queryError = summaryError as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isUnauth) {
    return (
      <div className={styles.container}>
        <PageHeader title="Proof Chain" subtitle="Tamper-proof proof records automatically generated from every enforcement decision" icon={<DocumentCheckmarkRegular />} showAIButton={false} />
        <UnauthenticatedBanner feature="evidence records" />
      </div>
    );
  }

  if (summaryError || (!summaryLoading && !summary)) {
    return (
      <div className={styles.container}>
        <PageHeader title="Proof Chain" subtitle="Tamper-proof proof records automatically generated from every enforcement decision" icon={<DocumentCheckmarkRegular />} showAIButton={false} />
        <Card>
          <div style={{ padding: tokens.spacingVerticalXXL }}>
            <Title2 style={{ marginBottom: tokens.spacingVerticalL }}>Evidence Pipeline</Title2>
            <Body1 style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalL }}>
              Continuous evidence generation begins once enforcement policies are active.
            </Body1>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', marginBottom: tokens.spacingVerticalL }}>
              {[
                { label: 'SDLC Event', status: 'pending' },
                { label: 'Policy Evaluation', status: 'pending' },
                { label: 'Enforcement Decision', status: 'pending' },
                { label: 'Evidence Record', status: 'pending' },
                { label: 'Framework Mapping', status: 'pending' },
              ].map((stage, i) => (
                <div key={stage.label} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                  <div style={{
                    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
                    borderRadius: tokens.borderRadiusMedium,
                    backgroundColor: tokens.colorNeutralBackground2,
                    border: `1px solid ${tokens.colorNeutralStroke2}`,
                  }}>
                    <Body1 className="metric-value" style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
                      {stage.label}
                    </Body1>
                  </div>
                  {i < 4 && <span style={{ color: tokens.colorNeutralForeground4, fontSize: '1.2rem' }}>→</span>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, color: tokens.colorNeutralForeground3 }}>
              <Body1>1. Connect delivery systems (GitHub, GitLab, CI/CD)</Body1>
              <Body1>2. Deploy enforcement policies</Body1>
              <Body1>3. Evidence streams automatically</Body1>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <PageHeader
        title="Proof Chain"
        subtitle="Tamper-proof proof records automatically generated from every enforcement decision"
        icon={<DocumentCheckmarkRegular />}
        actions={
          <div className={styles.headerActions}>
            {/* Date Preset Selector */}
            <Dropdown
              value={datePreset === 'custom' ? 'Custom Range' : datePreset === '7d' ? 'Last 7 Days' : datePreset === '30d' ? 'Last 30 Days' : 'This Month'}
              onOptionSelect={(_, data) => setDatePreset(data.optionValue as DatePreset)}
            >
              <Option value="7d">Last 7 Days</Option>
              <Option value="30d">Last 30 Days</Option>
              <Option value="month">This Month</Option>
              <Option value="custom">Custom Range</Option>
            </Dropdown>

            {datePreset === 'custom' && (
              <>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(_, data) => setCustomFrom(data.value)}
                />
                <span>to</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(_, data) => setCustomTo(data.value)}
                />
              </>
            )}

            <Button
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              onClick={() => setExportDialogOpen(true)}
            >
              Export Evidence
            </Button>
          </div>
        }
      />

      {/* Chain Verification Status */}
      {chainVerification && (
        <div
          className={`${styles.chainStatus} ${
            chainVerification.isValid ? styles.chainValid : styles.chainInvalid
          }`}
        >
          <LinkRegular />
          <Body1>
            {chainVerification.isValid ? (
              <>
                <strong>Evidence Chain Verified</strong> — All {chainVerification.evaluationsVerified} evaluations have valid cryptographic hashes
              </>
            ) : (
              <>
                <strong>Chain Integrity Warning</strong> — {chainVerification.brokenLinks.length} broken links detected
              </>
            )}
          </Body1>
        </div>
      )}

      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <Card className={styles.summaryCard}>
          <div className={styles.summaryValue}>{summary?.totalEvaluations ?? 0}</div>
          <div className={styles.summaryLabel}>Total Evaluations</div>
          <div className={styles.decisionBadges}>
            <Badge appearance="filled" color="success">
              <CheckmarkCircleRegular /> {summary?.byDecision?.Allow ?? 0} Allow
            </Badge>
            <Badge appearance="filled" color="warning">
              <WarningRegular /> {summary?.byDecision?.Warn ?? 0} Warn
            </Badge>
            <Badge appearance="filled" color="danger">
              <DismissCircleRegular /> {summary?.byDecision?.Block ?? 0} Block
            </Badge>
          </div>
        </Card>

        <Card className={styles.summaryCard}>
          <div className={styles.summaryValue}>
            {Object.keys(summary?.byFramework ?? {}).length}
          </div>
          <div className={styles.summaryLabel}>Frameworks Evaluated</div>
          <div className={styles.decisionBadges}>
            {Object.entries(summary?.byFramework ?? {}).slice(0, 3).map(([code, count]) => (
              <Badge key={code} appearance="outline">{code}: {count}</Badge>
            ))}
          </div>
        </Card>

        <Card className={styles.summaryCard}>
          <div className={styles.summaryValue}>
            {chainVerification?.isValid ? '✓' : '⚠'}
          </div>
          <div className={styles.summaryLabel}>Chain Integrity</div>
          <Body1 style={{ marginTop: tokens.spacingVerticalXS }}>
            {chainLoading ? 'Verifying...' : chainVerification?.isValid ? 'Verified' : 'Issues Found'}
          </Body1>
        </Card>

        <Card className={styles.summaryCard}>
          <div className={styles.summaryValue}>
            {summary?.totalEvaluations ?? 0}
          </div>
          <div className={styles.summaryLabel}>Evidence Volume (24h)</div>
          <Body1 style={{ marginTop: tokens.spacingVerticalXS, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
            Signed: ed25519 / LeftShield Evidence Authority
          </Body1>
        </Card>
      </div>

      {/* Tabs */}
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value as typeof selectedTab)}
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="records">Evidence Records</Tab>
      </TabList>

      {/* Content */}
      {selectedTab === 'overview' && (
        <>
          <Card>
            <CardHeader
              header={<Title2>Framework Distribution</Title2>}
              description="Evaluations by enforcement framework"
            />
            <div style={{ padding: tokens.spacingVerticalM }}>
              {Object.entries(summary?.byFramework ?? {}).map(([framework, count]) => {
                const total = summary?.totalEvaluations ?? 1;
                const percentage = (count / total) * 100;
                return (
                  <div key={framework} style={{ marginBottom: tokens.spacingVerticalM }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalXS }}>
                      <Body1>{framework}</Body1>
                      <Body1>{count} ({percentage.toFixed(1)}%)</Body1>
                    </div>
                    <ProgressBar value={percentage / 100} thickness="large" />
                  </div>
                );
              })}
              {Object.keys(summary?.byFramework ?? {}).length === 0 && (
                <Body1>No evaluations in selected date range</Body1>
              )}
            </div>
          </Card>

          {/* Evidence Chain Visualizer */}
          <Card>
            <CardHeader
              header={<Title2>Evidence Chain</Title2>}
              description="Visual representation of the cryptographic evidence chain"
            />
            <div style={{ padding: tokens.spacingVerticalM }}>
              <EvidenceChainVisualizer />
            </div>
          </Card>
        </>
      )}

      {selectedTab === 'records' && (
        <div className={styles.listSection}>
          <ListPageLayout
            toolbar={
              <>
                <div className={styles.recordsHeading}>
                  <Title2>Evidence Records</Title2>
                  <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
                    Detailed evaluation history with cryptographic proofs
                  </Body1>
                </div>
                <ListToolbar
                  search={state.search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search records…"
                  onRefresh={refetchRecords}
                  isRefreshing={recordsFetching}
                  testIdPrefix="evidence-records"
                />
              </>
            }
            footer={
              <Pager
                page={state.page}
                totalPages={recordsTotalPages}
                hasPreviousPage={recordsHasPreviousPage}
                hasNextPage={recordsHasNextPage}
                totalCount={recordsTotalCount}
                onPageChange={setPage}
                testId="evidence-records-pager"
              />
            }
          >
            {recordsLoading ? (
              <LoadingSkeleton rows={6} columns={6} />
            ) : recordsIsError ? (
              <ErrorState message="Unable to load evidence records." onRetry={refetchRecords} />
            ) : evidenceRecords.length === 0 ? (
              <EmptyState
                icon={<DocumentCheckmarkRegular />}
                title="No Evidence Records"
                description="No enforcement evaluations were recorded during the selected date range."
              />
            ) : (
              <ListLoadingOverlay busy={recordsFetching && !recordsLoading}>
                <Table aria-label="Evidence records">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell
                        sortable
                        sortDirection={
                          sortField === 'timestamp' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                        }
                        sortIcon={sortIcon('timestamp')}
                        onClick={() => toggleSort('timestamp')}
                      >
                        Timestamp
                      </TableHeaderCell>
                      <TableHeaderCell
                        sortable
                        sortDirection={sortField === 'type' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                        sortIcon={sortIcon('type')}
                        onClick={() => toggleSort('type')}
                      >
                        Type
                      </TableHeaderCell>
                      <TableHeaderCell
                        sortable
                        sortDirection={
                          sortField === 'decision' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                        }
                        sortIcon={sortIcon('decision')}
                        onClick={() => toggleSort('decision')}
                      >
                        Decision
                      </TableHeaderCell>
                      <TableHeaderCell style={{ width: '100%' }}>Frameworks</TableHeaderCell>
                      <TableHeaderCell
                        sortable
                        sortDirection={
                          sortField === 'violationCount' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                        }
                        sortIcon={sortIcon('violationCount')}
                        onClick={() => toggleSort('violationCount')}
                      >
                        Violations
                      </TableHeaderCell>
                      <TableHeaderCell>Evidence Hash</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evidenceRecords.map((item) => {
                      const decisionColor =
                        item.decision === 'Allow' ? 'success' : item.decision === 'Warn' ? 'warning' : 'danger';
                      return (
                        <TableRow key={item.id}>
                          <TableCell>{format(new Date(item.timestamp), 'MMM d, yyyy HH:mm')}</TableCell>
                          <TableCell>
                            <Badge appearance="outline">{item.type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge appearance="filled" color={decisionColor}>
                              {item.decision}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.frameworks.join(', ') || '-'}</TableCell>
                          <TableCell>{item.violationCount}</TableCell>
                          <TableCell>
                            <Tooltip content={item.hash} relationship="label">
                              <Text style={{ fontFamily: 'monospace' }}>{item.hash.substring(0, 12)}...</Text>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ListLoadingOverlay>
            )}
          </ListPageLayout>
        </div>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={(_, data) => setExportDialogOpen(data.open)}>
        <DialogSurface className={styles.exportDialog}>
          <DialogBody>
            <DialogTitle>Export Evidence</DialogTitle>
            <DialogContent>
              <Body1>
                Export enforcement evidence for the period {format(from, 'MMM d, yyyy')} to {format(to, 'MMM d, yyyy')}
              </Body1>
              <div className={styles.formatOptions}>
                <Field label="Export Format">
                  <Dropdown
                    value={exportFormat.toUpperCase()}
                    onOptionSelect={(_, data) => setExportFormat(data.optionValue as ExportFormat)}
                  >
                    <Option value="json">JSON - Machine-readable format</Option>
                    <Option value="csv">CSV - Spreadsheet format</Option>
                    <Option value="sarif">SARIF - Static analysis format</Option>
                    <Option value="pdf">PDF - Printable report</Option>
                  </Dropdown>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setExportDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<ArrowDownloadRegular />}
                onClick={() => exportMutation.mutate(exportFormat)}
                disabled={exportMutation.isPending}
              >
                {exportMutation.isPending ? 'Exporting...' : 'Export'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default EvidencePage;
